<template>
  <div>
    <v-dialog v-model="show" max-width="500">
      <v-card>
        <v-card-title class="headline">{{ $t('components.dialog.userEdit.editAccount') }}</v-card-title>

        <v-card-text>
          <v-form v-model="contactValid" ref="form">
            <v-radio-group
              v-if="!model.vendor"
              v-model="model.user_type"
              :label="$t('components.dialog.userEdit.selectUserType')"
              row
              @change="$refs.form.resetValidation()"
            >
              <v-radio :label="$t('components.dialog.userEdit.editorial')" value="editorial_staff"></v-radio>
              <v-radio :label="$t('components.dialog.userEdit.freelance')" value="freelance"></v-radio>
            </v-radio-group>
            <v-text-field
              v-if="!model.vendor"
              v-model="model.company_name"
              :rules="requiredRules"
              :label="$t('components.dialog.userEdit.company')"
            ></v-text-field>
            <v-text-field
              v-if="!model.vendor"
              v-model="model.publisher"
              :rules="[requiredBasedOnUserType]"
              :label="$t('components.dialog.userEdit.publisher') + (isFieldRequired() ? ' *' : '')"
            ></v-text-field>
            <v-text-field
              v-model="model.name"
              :rules="requiredRules"
              :label="$t('components.dialog.userEdit.name')"
            ></v-text-field>
            <single-country-selector-component
              v-if="useCountries"
              v-model="model.country"
              :otherCountries="model.otherCountries"
              :countries="lookupStore.countries"
              :label="$t('components.dialog.userEdit.selectCountry')"
              :multipleCountriesLabel="$t('components.dialog.userEdit.otherMultipleCountriesSelected')"
              @onCountrySelected="model.countries = $event"
              required
            ></single-country-selector-component>
            <v-text-field
              v-model="model.email"
              readonly
              disabled
              :label="$t('components.dialog.userEdit.email')"
            ></v-text-field>

            <v-checkbox
              v-model="model.changePassword"
              :label="$t('components.dialog.userEdit.changePassword')"
              class="mt-0 pt-0"
            ></v-checkbox>
            <div v-if="model.changePassword">
              <v-text-field
                v-model="model.password"
                :rules="passwordRules"
                @input="onPasswordInput"
                type="password"
                :label="$t('components.dialog.userEdit.password')"
              ></v-text-field>
              <v-text-field
                v-model="model.confirmPassword"
                :rules="passwordConfirmRules"
                type="password"
                :label="$t('components.dialog.userEdit.confirmPassword')"
              ></v-text-field>
            </div>
          </v-form>

          <p class="text-error" v-if="showSignupFailedError">{{ $t('components.dialog.userEdit.editAccountError') }}</p>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn text @click="cancel()">
            {{ $t('common.cancel') }}
          </v-btn>

          <v-btn color="primary" class="black--text" :disabled="!isFormValid()" @click="save()" :loading="saving">
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { UserService } from '../../services/user.service';
import SingleCountrySelectorComponent from '@shared/components/SingleCountrySelectorComponent.vue';
import { useLookupStore } from '../../stores/LookupStore';
import { useUserStore } from '../../stores/UserStore';

export default {
  components: {
    SingleCountrySelectorComponent,
  },

  props: {
    visible: Boolean,
    useCountries: {
      type: Boolean,
      default: true,
    }
  },

  model: {
    prop: 'visible',
    event: 'update',
  },

  data() {
    return {
      model: {},
      contactValid: false,
      showSignupFailedError: false,
      emailExistsError: false,
      showLicenseDialog: false,
      confirmPasswordNotMatchError: false,
      saving: false,

      //rules
      requiredRules: [(v) => !!v || this.$i18n.t('components.dialog.userEdit.mandatory')],
      passwordRules: [(v) => (!!v && v.length >= 6) || this.$i18n.t('components.dialog.userEdit.mandatoryAndMinChars')],
      passwordConfirmRules: [
        (value) => !!value || this.$i18n.t('components.dialog.userEdit.mandatoryAndMinChars'),
        () =>
          this.model.password === this.model.confirmPassword ||
          this.$i18n.t('components.dialog.userEdit.passwordNotMatch'),
      ],
    };
  },

  watch: {
    visible: function (newValue) {
      const { countries, ...userValues } = this.userStore.user;
      const [country, ...restCountries] = countries;
      this.model = newValue ? { ...userValues, countries, country, otherCountries: restCountries } : {};
    },
  },

  computed: {
    ...mapStores(useLookupStore, useUserStore),

    show: {
      get: function () {
        return this.visible;
      },
      set: function (value) {
        this.$emit('update', value);

        if (value === false) {
          this.$emit('close');
        }
      },
    },
  },

  created() {
    this.clearFields();
    this.lookupStore.loadCountries();
  },

  methods: {
    requiredBasedOnUserType(value) {
      if (this.isFieldRequired()) {
        return !!value || this.$i18n.t('components.dialog.userEdit.mandatory');
      }
      return true;
    },

    onPasswordInput() {
      this.model.confirmPassword = undefined;
    },

    isFieldRequired() {
      return this.model.user_type === 'editorial_staff';
    },

    save() {
      if (!this.model.changePassword) {
        delete this.model.password;
        delete this.model.confirmPassword;
      }

      this.saving = true;

      new UserService()
        .update(this.model)
        .then(() => {
          this.cancel();
          this.$emit('success');
          this.saving = false;
        })
        .catch((err) => {
          const error = err?.response ? err.response.data : {};
          if (error?.message === 'validation.unique' && !!error?.errors?.email) {
            this.emailExistsError = true;
          }
          this.showSignupFailedError = true;
          this.saving = false;
        });
    },

    cancel() {
      this.clearFields();
      this.$refs.form.reset();
      this.showSignupFailedError = false;
      this.emailExistsError = false;
      this.contactValid = false;
      this.show = false;
    },

    clearFields() {
      this.model = { user_type: 'editorial_staff' };
    },

    isFormValid() {
      return this.contactValid;
    },
  },
};
</script>
